import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { SharedAssessmentData } from '@/lib/shared-data';

interface AssessmentFooterProps {
  currentTouchpoint: { name: string } | null;
  assessmentData: SharedAssessmentData;
  isAllAnswered: boolean;
  onSubmitAssessment: () => void;
  onSaveAsDraft?: () => void;
  isPending?: boolean;
}

export function AssessmentFooter({
  currentTouchpoint,
  assessmentData,
  isAllAnswered,
  onSubmitAssessment,
  onSaveAsDraft,
  isPending = false,
}: AssessmentFooterProps) {
  const { t, i18n } = useTranslation();
  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);

  if (!currentTouchpoint) return null;

  const allStandards = assessmentData.titles.flatMap(title => title.standards);
  const answeredCount = allStandards.filter(s => s.status !== null).length;

  return (
    <div className="bg-white border-t p-3 sm:p-4">
      <div className="max-w-7xl mx-auto">
        {/* Mobile: Show current touchpoint info */}
        <div className="lg:hidden mb-3 p-2 bg-blue-50 rounded-lg">
          <div className="text-xs font-medium text-blue-900 truncate">
            {currentTouchpoint.name}
          </div>
          <div className="text-xs text-blue-700">
            {answeredCount} / {allStandards.length} answered
          </div>
        </div>

        <div
          className={cn(
            'flex flex-col xs:flex-row xs:items-center xs:justify-between gap-3',
            isRTL ? 'xs:flex-row-reverse' : 'xs:flex-row'
          )}
        >
          <div className="hidden lg:block text-gray-700 text-xs sm:text-sm">
            {t('assessments.perform.progress')}: {answeredCount} of{' '}
            {allStandards.length} {t('assessments.perform.questionsAnswered')}
          </div>

          <div
            className={cn(
              'flex gap-2',
              isRTL ? 'flex-row-reverse' : 'flex-row'
            )}
          >
            <Button
              className="bg-primary hover:bg-primary/90 active:bg-primary/80 flex-1 xs:flex-none text-xs sm:text-sm px-4 py-3 min-h-[44px] h-11 touch-manipulation transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={
                isAllAnswered
                  ? onSubmitAssessment
                  : onSaveAsDraft || onSubmitAssessment
              }
              size="sm"
              disabled={isPending}
            >
              {isPending
                ? t('submitting', 'Submitting...')
                : isAllAnswered
                  ? t(
                      'assessments.perform.submitAssessment',
                      'Submit Assessment'
                    )
                  : t('assessments.perform.saveAsDraft', 'Save as Draft')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
